import { useState } from "react";
import { useEffect } from "react";

import UserIcon from "@mui/icons-material/AccountCircle";
import AssignmentRoundedIcon from "@mui/icons-material/AssignmentRounded";
import DeleteIcon from "@mui/icons-material/Delete";
import BioIcon from "@mui/icons-material/Fingerprint";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import NotesRoundedIcon from "@mui/icons-material/NotesRounded";
import DocumentIcon from "@mui/icons-material/Source";
import { InputAdornment, Tab, useMediaQuery } from "@mui/material";
import { Typography } from "@mui/material";
import Button from "@mui/material/Button";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import Grid from "@mui/material/Grid";
import { useTheme } from "@mui/material/styles";
import { v4 as uuid } from "uuid";

import constants from "../../assets/constants";
import { getAllAssessmentByRelatedID } from "../../data/omniDataAPI";
import { createNewRelationship } from "../../data/omniDataModel";
import { deepCopyObject, isValidEmail } from "../../utils/helpers";
import Checkbox from "../ui/omniCheckbox";
import OmniDatePicker from "../ui/omniDatePicker";
import Dialog from "../ui/omniDialog";
import DialogActions from "../ui/omniDialogActions";
import DialogContent from "../ui/omniDialogContent";
import DialogList from "../ui/omniDialogList";
import DialogTab from "../ui/omniDialogTab";
import DialogTabs from "../ui/omniDialogTabs";
import DialogTitle from "../ui/omniDialogTitle";
import OmniEditButton from "../ui/omniEditButton";
import OmniMenuItem from "../ui/omniMenuItem";
import OmniPhoneNumberField from "../ui/omniPhoneNumberField";
import OmniResponsiveButton from "../ui/omniResponsiveButton";
import OmniTextField from "../ui/omniTextField";
import OmniUserAssessmentListItem from "../ui/omniUserAssessmentListItem";
import AlertDialog from "./alertDialog";
import AssessmentWizardDialog from "./assessmentWizardDialog";

const UserDialog = props => {
  const {
    open,
    dialogTitle = "User",
    okMessage = "OK",
    cancelMessage = "Cancel",
    deleteMessage = "Remove",
    maxWidth = "sm",
    fullWidth = true,
    omniODM, //omni Data Model Object, a root level object representation, Pass in a COPY, the only way this works
    providerId = null, //this is used to associate this with a
    tabId = 0,
    organizationType = constants.organizationTypeEnums.omnisicaHealth,
  } = props;

  const [editedDMO, setEditedDMO] = useState(omniODM);
  const [editedAddress, setEditedAddress] = useState(omniODM.omniData.address);

  /* eslint-disable no-unused-vars */
  const [editedPatientData, setEditedPatientData] = useState(omniODM.omniData.patientData);
  const [editedPartnerData, setEditedPartnerData] = useState(omniODM.omniData.partnerData);
  const [editedProviderData, setEditedProviderData] = useState(omniODM.omniData.providerData);
  /* eslint-disable no-unused-vars */

  // Assessment list support
  const [userAssessmentODMs, setUserAssessmentODMs] = useState(null);
  const [assessmentODMs, setAssessmentODMs] = useState(null);
  const [selectedAssessmentODM, setSelectedAssessmentODM] = useState(null);
  const [showAssessmentWizardDialog, setShowAssessmentWizardDialog] = useState(false);
  const [sentFromListItem, setSentFromListItem] = useState(false);

  // Required Fields and Validation
  const [readyOk, setReadyOk] = useState(false);
  const [givenNameValid, setGivenNameValid] = useState(false);
  const [familyNameValid, setFamilyNameValid] = useState(false);
  const [mobilePhoneValid, setMobilePhoneValid] = useState(false);
  const [emailValid, setEmailValid] = useState(false);
  const [birthdate, setBirthdate] = useState(
    editedDMO.omniData.birthdate ? new Date(editedDMO.omniData.birthdate) : new Date(),
  );
  const [birthdateValid, setBirthdateValid] = useState(false);

  //#region custom Input Handlers
  const [lockDisplayName, setLockDisplayName] = useState(true);

  //#endregion

  const [monitorUser, setMonitorUser] = useState(false);

  const updateUserDMO = event => {
    try {
      editedDMO.omniData[event.target.id] = event.target.value;

      if (lockDisplayName) {
        let newDisplayName =
          (editedDMO.omniData.nameTitle || "") +
          " " +
          (editedDMO.omniData.givenName || "") +
          " " +
          (editedDMO.omniData.middleName || "") +
          " " +
          (editedDMO.omniData.familyName || "") +
          " " +
          (editedDMO.omniData.nameSuffix || "");
        editedDMO.omniData.displayName = newDisplayName.replace(/  +/g, " ").trim();
      }
      setEditedDMO(deepCopyObject(editedDMO));
    } catch (error) {
      console.log(error);
    }
  };

  const updateAddressDMO = event => {
    try {
      editedAddress[event.target.id] = event.target.value;

      setEditedAddress(deepCopyObject(editedAddress));
    } catch (error) {
      console.log(error);
    }
  };

  const updatePatientDMO = event => {
    try {
      editedPatientData[event.target.id] = event.target.value;

      setEditedPatientData(deepCopyObject(editedPatientData));
    } catch (error) {
      console.log(error);
    }
  };

  const [showConfirm, setShowConfirm] = useState(false);
  const [tabIndex, setTabIndex] = useState(tabId);

  const onDeleteHandler = () => {
    //doesn't matter, ID's arent changed
    props.onDelete(editedDMO._id);
  };

  const onAcceptHandler = () => {
    //notice we didn't touch the id
    editedDMO.omniData.address = editedAddress;
    editedDMO.omniData.patientData = editedPatientData;
    editedDMO.omniData.partnerData = editedPartnerData;
    editedDMO.omniData.providerData = editedProviderData;

    if (birthdateValid) {
      editedDMO.omniData.birthdate = birthdate.toISOString();
    }
    if (monitorUser && providerId) {
      //make sure its there
      const filtered = omniODM.omniRelationships.relatedData.filter(
        element =>
          element._id === providerId &&
          element.type === constants.onboardingUserType.provider &&
          element.omniRelationshipType === constants.omniRelationshipTypes.patientPartner,
      );
      if (filtered.length === 0) {
        //create a new relationship
        let newRelationship = createNewRelationship(constants.omniRelationshipTypes.patientPartner);
        newRelationship.type = constants.onboardingUserType.provider;
        newRelationship._id = providerId;

        omniODM.omniRelationships.relatedData.push(newRelationship);
      }
    } else {
      const filteredRelationships = omniODM.omniRelationships.relatedData.filter(
        element =>
          !(
            element._id === providerId &&
            element.type === constants.onboardingUserType.provider &&
            element.omniRelationshipType === constants.omniRelationshipTypes.patientPartner
          ),
      );

      omniODM.omniRelationships.relatedData = filteredRelationships;
    }

    props.onAccept(editedDMO);
  };

  const onCloseHandler = () => {
    props.onClose();
  };

  // Check birthdate
  useEffect(() => {
    if (birthdate != null) {
      setBirthdateValid(true);
    } else {
      setBirthdateValid(false);
    }
  }, [birthdate]);

  // Check mobile phone
  useEffect(() => {
    editedDMO.omniData.mobilePhone &&
    RegExp(/^\D*(\d\D*){10}$/).exec(editedDMO.omniData.mobilePhone)
      ? setMobilePhoneValid(true)
      : setMobilePhoneValid(false);
  }, [editedDMO.omniData.mobilePhone]);

  // Check email
  useEffect(() => {
    isValidEmail(editedDMO.omniData.email) ? setEmailValid(true) : setEmailValid(false);
  }, [editedDMO.omniData.email]);

  useEffect(() => {
    givenNameValid &&
    familyNameValid &&
    (editedPatientData != null || (mobilePhoneValid && emailValid))
      ? setReadyOk(true)
      : setReadyOk(false);
  }, [givenNameValid, familyNameValid, mobilePhoneValid, emailValid, editedPatientData]);

  // Get User Assessments
  useEffect(() => {
    if (omniODM != null && assessmentODMs == null) {
      const getAssessments = async () => {
        try {
          let result = await getAllAssessmentByRelatedID(omniODM._id);

          setAssessmentODMs(result);
        } catch (error) {
          console.error("Error fetching assessment data:", error);
        }
      };
      getAssessments();
    } else if (omniODM != null && assessmentODMs != null) {
      setUserAssessmentODMs(assessmentODMs);
    }

    if (omniODM != null && providerId != null) {
      const filtered = omniODM.omniRelationships.relatedData.filter(
        element =>
          element._id === providerId &&
          element.type === constants.onboardingUserType.provider &&
          element.omniRelationshipType === constants.omniRelationshipTypes.patientPartner,
      );
      if (filtered.length > 0) {
        setMonitorUser(true);
      }
    }
  }, [omniODM, providerId, assessmentODMs]);

  const theme = useTheme();

  const smallScreen = useMediaQuery(theme => theme.breakpoints.down("sm"));

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason !== "backdropClick") {
          onCloseHandler();
        }
      }}
      disableEscapeKeyDown
      maxWidth={maxWidth}
      fullWidth={fullWidth}
    >
      <DialogTitle onClose={onCloseHandler}>{dialogTitle}</DialogTitle>
      <DialogTabs tabIndex={tabIndex} setTabIndex={setTabIndex}>
        <Tab key={`tab0`} icon={<UserIcon />} label={`Identity`} />
        <Tab key={`tab1`} icon={<BioIcon />} label={"Personal"} />
        {editedPatientData != null && (
          <Tab key={`tab2`} icon={<NotesRoundedIcon />} label={"Notes"} />
        )}
        {organizationType !== constants.organizationTypeEnums.botpressTesting && (
          <>
            <Tab key={`tab3`} icon={<AssignmentRoundedIcon />} label={"Assessments"} />
            <Tab key={`tab4`} icon={<DocumentIcon />} label={"Documents"} />
          </>
        )}
      </DialogTabs>
      <DialogContent
        sx={[
          !smallScreen && { minHeight: "326px", maxHeight: "326px" },
          tabIndex === 3 && { padding: 0 },
        ]}
      >
        {/* USER INFORMATION TAB */}
        <DialogTab selectedIndex={tabIndex} index={0}>
          <Typography
            variant="overline"
            sx={{ marginTop: theme.spacing(1), marginBottom: `-${theme.spacing(1)}` }}
          >
            {`NAME`}
          </Typography>

          <Grid container spacing={1}>
            <Grid container spacing={1} size={12}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <OmniTextField
                  fullWidth
                  label="First"
                  type="textInput"
                  name={uuid()} //prevents AutoComplete Suggestions
                  variant="outlined"
                  id={"givenName"}
                  value={editedDMO.omniData.givenName}
                  error={!givenNameValid}
                  onChange={event => {
                    event.target.value = event.target.value.trim();
                    updateUserDMO(event);
                  }}
                  placeholder={"First Name"}
                  required
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <OmniTextField
                  fullWidth
                  label="Last"
                  type="textInput"
                  name={uuid()} //prevents AutoComplete Suggestions
                  variant="outlined"
                  id={"familyName"}
                  value={editedDMO.omniData.familyName}
                  error={!familyNameValid}
                  onChange={event => {
                    event.target.value = event.target.value.trim();
                    updateUserDMO(event);
                  }}
                  placeholder={"Last Name"}
                  required
                />
              </Grid>
            </Grid>
            <Grid container spacing={1} size={12}>
              {organizationType === constants.organizationTypeEnums.empireOS && (
                <Grid size={{ xs: 6 }}>
                  <OmniTextField
                    label="Business Pillar"
                    name={uuid()} //prevents AutoComplete Suggestions
                    variant="outlined"
                    id={"gender"}
                    key={"gender"}
                    value={editedDMO.omniData.businessPillar}
                    select
                    onChange={event => {
                      event.target["id"] = "businessPillar";
                      updateUserDMO(event);
                    }}
                  >
                    {constants.empirePillarTypes.map(empirePillar => (
                      <OmniMenuItem key={empirePillar.value} value={empirePillar.value}>
                        {empirePillar.label}
                      </OmniMenuItem>
                    ))}
                  </OmniTextField>
                </Grid>
              )}

              <Grid
                size={{
                  xs: organizationType === constants.organizationTypeEnums.empireOS ? 6 : 12,
                }}
              >
                <OmniTextField
                  fullWidth
                  label="Display Name"
                  type="textInput"
                  variant="outlined"
                  name={uuid()} //prevents AutoComplete Suggestions
                  id={"displayName"}
                  value={editedDMO.omniData.displayName}
                  onChange={event => {
                    event.target.value = event.target.value.trim();
                    updateUserDMO(event);
                  }}
                  disabled={lockDisplayName}
                  placeholder={"Your alias in our system"}
                  slotProps={{
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">
                          {" "}
                          <OmniEditButton
                            title={lockDisplayName ? "Unlock Display Name" : "Lock Display Name"}
                            onClick={e => {
                              e.stopPropagation();
                              setLockDisplayName(!lockDisplayName);
                            }}
                            icon={
                              lockDisplayName ? (
                                <LockIcon size="large" />
                              ) : (
                                <LockOpenIcon size="large" />
                              )
                            }
                          />
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              </Grid>
            </Grid>

            <Grid container size={12}>
              <Typography
                variant="overline"
                sx={{ marginTop: theme.spacing(1), marginBottom: `-${theme.spacing(1)}` }}
              >
                Contact Information
              </Typography>
              <Grid container spacing={1} size={12}>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <OmniPhoneNumberField
                    fullWidth
                    label="Mobile Number"
                    name={uuid()} //prevents AutoComplete Suggestions
                    variant="outlined"
                    id={"mobilePhone"}
                    value={editedDMO.omniData.mobilePhone}
                    error={!mobilePhoneValid}
                    onChange={event => {
                      updateUserDMO(event);
                    }}
                    onFocus={event => event.target.select()}
                    placeholder={"Your Phone Number"}
                    required={!editedPatientData}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 8 }}>
                  <OmniTextField
                    fullWidth
                    label="Email"
                    type="textField"
                    name={uuid()} //prevents AutoComplete Suggestions
                    variant="outlined"
                    id={"email"}
                    value={editedDMO.omniData.email}
                    error={!emailValid}
                    onChange={event => {
                      updateUserDMO(event);
                    }}
                    placeholder={"Primary Email Address"}
                    required={!editedPatientData}
                  />
                </Grid>
              </Grid>
            </Grid>

            {providerId && (
              <Grid container size={12}>
                <Typography
                  variant="overline"
                  sx={{ marginTop: theme.spacing(1), marginBottom: `-${theme.spacing(0.5)}` }}
                >
                  Candidate Tracking
                </Typography>
                <Grid container spacing={1} size={12}>
                  <FormGroup sx={{ marginLeft: theme.spacing(2) }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          color="secondary"
                          checked={monitorUser}
                          onChange={() => setMonitorUser(!monitorUser)}
                          name="monitorUser"
                        />
                      }
                      label="I am actively monitoring this candidate."
                    />
                  </FormGroup>
                </Grid>
              </Grid>
            )}
          </Grid>
        </DialogTab>

        {/* USER DEMOGRAPHICS TAB */}
        <DialogTab selectedIndex={tabIndex} index={1}>
          <Typography
            variant="overline"
            sx={{ marginTop: theme.spacing(1), marginBottom: `-${theme.spacing(1)}` }}
          >
            Demographics
          </Typography>{" "}
          <Grid container size={12}>
            <Grid container spacing={1} size={12}>
              <Grid size={{ xs: 12, sm: 4 }}>
                <OmniDatePicker
                  id={"birthdate"}
                  label="Birthdate"
                  value={birthdate}
                  sx={{
                    "& .MuiInputBase-input": { borderColor: theme.palette.error.main },
                    "& .MuiPickersInputBase-root": { height: "40px" },
                  }}
                  name={uuid()} //prevents AutoComplete Suggestions
                  variant="inline"
                  inputVariant="outlined"
                  onAccept={null}
                  openTo={"year"}
                  disabled={false}
                  placeholder={"Date of Birth"}
                  helperText={""}
                  disableFuture
                  fullWidth
                  error={!birthdateValid}
                  onChange={event => {
                    let dateValue = null;
                    if (event && event.toString() !== "Invalid Date") {
                      dateValue = `${event.toISOString()}`;
                    } else {
                      setBirthdateValid(false);
                    }
                    setBirthdate(event);
                  }}
                  onError={err => {
                    err ? setBirthdateValid(false) : setBirthdateValid(true);
                  }}
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 8 }}>
                <OmniTextField
                  label="Race"
                  type="textInput"
                  name={uuid()} //prevents AutoComplete Suggestions
                  variant="outlined"
                  id={"race"}
                  value={editedDMO.omniData.race}
                  select
                  onChange={event => {
                    event.target["id"] = "race";
                    updateUserDMO(event);
                  }}
                  placeholder={"Race"}
                  error={!editedDMO.omniData.race}
                >
                  {constants.userRaceOptions.map(race => (
                    <OmniMenuItem key={race.value} value={race.value}>
                      {race.label}
                    </OmniMenuItem>
                  ))}
                </OmniTextField>
              </Grid>

              <Grid size={{ xs: 12, sm: 4 }}>
                <OmniTextField
                  label="Gender"
                  name={uuid()} //prevents AutoComplete Suggestions
                  variant="outlined"
                  id={"gender"}
                  key={"gender"}
                  value={editedDMO.omniData.gender}
                  select
                  defaultValue="female"
                  onChange={event => {
                    event.target["id"] = "gender";
                    updateUserDMO(event);
                  }}
                  error={!editedDMO.omniData.gender}
                >
                  {constants.userGenderOptions.map(gender => (
                    <OmniMenuItem key={gender.value} value={gender.value}>
                      {gender.label}
                    </OmniMenuItem>
                  ))}
                </OmniTextField>
              </Grid>

              <Grid size={{ xs: 12, sm: 8 }}>
                <OmniTextField
                  label="Ethnicity"
                  name={uuid()} //prevents AutoComplete Suggestions
                  variant="outlined"
                  id={"ethnicity"}
                  key={"ethnicity"}
                  select
                  value={editedDMO.omniData.ethnicity}
                  defaultValue="false"
                  onChange={event => {
                    event.target["id"] = "ethnicity";
                    updateUserDMO(event);
                  }}
                  error={!editedDMO.omniData.ethnicity}
                >
                  {constants.userEthnicityOptions.map(ethnicity => (
                    <OmniMenuItem key={ethnicity.value} value={ethnicity.value}>
                      {ethnicity.label}
                    </OmniMenuItem>
                  ))}
                </OmniTextField>
              </Grid>
            </Grid>

            <Grid container size={12}>
              <Typography
                variant="overline"
                sx={{ marginTop: theme.spacing(1), marginBottom: `-${theme.spacing(1)}` }}
              >
                Address
              </Typography>
              <OmniTextField
                fullWidth
                label="Street"
                type="textInput"
                name={uuid()} //prevents AutoComplete Suggestions
                variant="outlined"
                id={"streetAddress"}
                value={editedAddress.streetAddress}
                onChange={event => {
                  updateAddressDMO(event);
                }}
                placeholder={"The street address for this user"}
              />
              <Grid container spacing={1} size={12}>
                <Grid size={{ xs: 12, md: 7 }}>
                  <OmniTextField
                    fullWidth
                    label="Locality"
                    type="textInput"
                    name={uuid()} //prevents AutoComplete Suggestions
                    variant="outlined"
                    id={"locality"}
                    value={editedAddress.locality}
                    onChange={event => {
                      updateAddressDMO(event);
                    }}
                    placeholder={"City Name"}
                  />
                </Grid>
                <Grid size={{ xs: 6, md: 2 }}>
                  <OmniTextField
                    fullWidth
                    label="Region"
                    type="textInput"
                    name={uuid()} //prevents AutoComplete Suggestions
                    variant="outlined"
                    id={"region"}
                    value={editedAddress.region}
                    onChange={event => {
                      updateAddressDMO(event);
                    }}
                    placeholder={"State"}
                  />
                </Grid>
                <Grid size={{ xs: 6, md: 3 }}>
                  <OmniTextField
                    fullWidth
                    label="Postal Code"
                    type="textInput"
                    name={uuid()} //prevents AutoComplete Suggestions
                    variant="outlined"
                    id={"postalCode"}
                    value={editedAddress.postalCode}
                    onChange={event => {
                      updateAddressDMO(event);
                    }}
                    placeholder={"zip"}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </DialogTab>

        {/* USER NOTES TAB */}
        {editedPatientData != null && (
          <DialogTab selectedIndex={tabIndex} index={2}>
            <Typography
              variant="overline"
              sx={{ marginTop: theme.spacing(1), marginBottom: `-${theme.spacing(1)}` }}
            >
              {`NOTES`}
            </Typography>{" "}
            <Grid container size={12}>
              <Grid size={{ xs: 12 }}>
                <OmniTextField
                  name={uuid()} //prevents AutoComplete Suggestions
                  variant="outlined"
                  id={"notes"}
                  key={"notes"}
                  label="Patient Notes"
                  rowCount="5"
                  multiline={true}
                  fullWidth
                  value={editedPatientData.notes}
                  onChange={event => {
                    event.target["id"] = "notes";
                    updatePatientDMO(event);
                  }}
                />
              </Grid>
            </Grid>
          </DialogTab>
        )}

        {/* USER ASSESSMENTS TAB */}
        <DialogTab selectedIndex={tabIndex} index={editedPatientData != null ? 3 : 2}>
          {Array.isArray(userAssessmentODMs) && (
            <DialogList sx={{ padding: theme.spacing(1) }}>
              {userAssessmentODMs
                .sort((a, b) => {
                  let dateA = new Date(a.omniData.completeDate);
                  let dateB = new Date(b.omniData.completeDate);
                  return dateA > dateB ? -1 : dateA < dateB ? 1 : 0;
                })
                .map((assessment, index) => {
                  return (
                    <OmniUserAssessmentListItem
                      key={`PartnerPatient-Assessments-${index}`}
                      assessmentODM={assessment}
                      onClickHandler={e => {
                        try {
                          setSentFromListItem(true);
                          setSelectedAssessmentODM(assessment);
                        } catch (error) {
                          console.log(error);
                        }
                      }}
                    />
                  );
                })}
            </DialogList>
          )}
        </DialogTab>

        {/* USER ASSESSMENTS TAB */}
        <DialogTab selectedIndex={tabIndex} index={editedPatientData != null ? 4 : 3}>
          {/* {Array.isArray(userAssessmentODMs) && (
            <DialogList sx={{ padding: theme.spacing(1) }}>
              {userAssessmentODMs
                .sort((a, b) => {
                  let dateA = new Date(a.omniData.completeDate);
                  let dateB = new Date(b.omniData.completeDate);
                  return dateA > dateB ? -1 : dateA < dateB ? 1 : 0;
                })
                .map((assessment, index) => {
                  return (
                    <OmniUserAssessmentListItem
                      key={`PartnerPatient-Assessments-${index}`}
                      assessmentODM={assessment}
                      onClickHandler={e => {
                        try {
                          setSentFromListItem(true);
                          setSelectedAssessmentODM(assessment);
                        } catch (error) {
                          console.log(error);
                        }
                      }}
                    />
                  );
                })}
            </DialogList>
          )} */}
        </DialogTab>
      </DialogContent>

      <DialogActions>
        <Grid container justifyContent="space-between" size={12} width={"100%"}>
          <Grid size={{ xs: 6 }}>
            {omniODM._id != null && props.onDelete && (
              <OmniResponsiveButton
                icon={<DeleteIcon />}
                onClick={() => setShowConfirm(true)}
                buttonText={deleteMessage}
                tooltipTitle=""
                // this is in here for some reason
                // disabled={tabIndex === 2}
              />
            )}
          </Grid>
          <Grid container justifyContent="flex-end" size={{ xs: 6 }} spacing={2}>
            <Grid size={"auto"}>
              {cancelMessage && (
                <Button onClick={onCloseHandler} color="secondary" variant="contained">
                  {cancelMessage}
                </Button>
              )}
            </Grid>
            <Grid size={"auto"}>
              <Button
                onClick={onAcceptHandler}
                color="secondary"
                disabled={!readyOk}
                variant="contained"
              >
                {okMessage}
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </DialogActions>

      {showConfirm && (
        <AlertDialog
          open={showConfirm}
          dialogTitle={`Confirm`}
          dialogMessage={"Are you sure you want to remove this user?"}
          onAccept={() => {
            onDeleteHandler();
            setShowConfirm(false);
          }}
          onClose={() => {
            setShowConfirm(false);
          }}
        />
      )}

      {(showAssessmentWizardDialog || selectedAssessmentODM) && (
        <AssessmentWizardDialog
          open={showAssessmentWizardDialog || selectedAssessmentODM}
          displayResultsAssessmentODM={selectedAssessmentODM}
          onAccept={() => {
            setSelectedAssessmentODM(null);
            setShowAssessmentWizardDialog(false);
          }}
          onClose={() => {
            setSelectedAssessmentODM(null);
            setShowAssessmentWizardDialog(false);
          }}
        />
      )}
    </Dialog>
  );
};

export default UserDialog;
